import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Trash2, KeyRound } from "lucide-react";
import { useConfirm } from "@/components/ConfirmDialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ConfirmReminders from "@/components/ConfirmReminders";

const inp = "px-2.5 py-1.5 rounded border text-sm";
const BASE = window.location.origin;

function Calendly({ isAdmin }) {
  const [s, setS] = useState(null);
  const [token, setToken] = useState("");
  const load = () => api.get("/calendly/settings").then((r) => setS(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => { try { await api.put("/calendly/settings", { token }); setToken(""); toast.success("Calendly connected"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const sub = async () => { try { await api.post("/calendly/subscribe"); toast.success("Webhooks on"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  return (
    <div className="panel p-4 space-y-2" data-testid="calendly-settings">
      <div className="text-sm font-medium">Calendly</div>
      <div className="text-xs text-neutral-500">{s?.token_set ? `Connected${s.user ? ` as ${s.user}` : ""}` : "Not connected"} · webhooks {s?.subscribed ? "on" : "off"}. Bookings write Slot Time, Meeting Type and link onto the lead; cancels log "Cancelled by lead", reschedules log "Rebooked".</div>
      {isAdmin && <div className="flex flex-wrap gap-2">
        <input type="password" className={`${inp} flex-1 min-w-[220px]`} placeholder="Personal access token" value={token} onChange={(e) => setToken(e.target.value)} data-testid="calendly-token-input" />
        <button className="btn" disabled={!token} onClick={save} data-testid="calendly-save-btn">Save token</button>
        <button className="btn" disabled={!s?.token_set} onClick={sub} data-testid="calendly-subscribe-btn">{s?.subscribed ? "Re-create webhook" : "Turn on webhooks"}</button>
      </div>}
    </div>
  );
}

function Fathom() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [s, setS] = useState(null);
  const [key, setKey] = useState("");
  const [users, setUsers] = useState([]);
  const [forEmail, setForEmail] = useState(user?.email || "");
  const load = () => api.get("/fathom/status").then((r) => setS(r.data));
  useEffect(() => { load(); if (isAdmin) api.get("/crm/users").then((r) => setUsers(r.data.rows.filter((u) => !/\(api\)/i.test(u.name || "")))); }, [isAdmin]);
  const connected = (s?.connected_users || []).includes(forEmail);
  const who = users.find((u) => u.email === forEmail)?.name || forEmail;
  const save = async (k) => { try { await api.put("/fathom/key", { key: k, for_email: forEmail }); setKey(""); toast.success(k ? `Fathom connected for ${who}` : `Fathom disconnected for ${who}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  return (
    <div className="panel p-4 space-y-2" data-testid="fathom-settings">
      <div className="text-sm font-medium">Fathom</div>
      <div className="text-xs text-neutral-500">Each person connects their own key (Fathom → Settings → API Access). Recordings show in the lead's Calls tab, matched by attendee email or lead name in the title. Khush's and Vedant's recordings are visible to everyone. Connected: <b data-testid="fathom-connected-users">{(s?.connected_users || []).join(", ") || "nobody yet"}</b>.</div>
      <div className="flex flex-wrap gap-2">
        {isAdmin && (
          <select className={inp} value={forEmail} onChange={(e) => setForEmail(e.target.value)} data-testid="fathom-user-select" title="Whose Fathom key is this?">
            {users.map((u) => <option key={u.email} value={u.email}>{u.name || u.email}{(s?.connected_users || []).includes(u.email) ? " · connected" : ""}</option>)}
          </select>
        )}
        <input type="password" className={`${inp} flex-1 min-w-[180px]`} placeholder={`Fathom API key for ${who}`} value={key} onChange={(e) => setKey(e.target.value)} data-testid="fathom-key-input" />
        <button className="btn" disabled={!key} onClick={() => save(key)} data-testid="fathom-save-btn">Connect</button>
        {connected && <button className="btn" onClick={() => save("")} data-testid="fathom-disconnect-btn">Disconnect</button>}
      </div>
    </div>
  );
}

function ApiKeys() {
  const { confirm } = useConfirm();
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [scope, setScope] = useState("admin");
  const [days, setDays] = useState("");
  const [shown, setShown] = useState(null);
  const load = () => api.get("/apikeys").then((r) => setRows(r.data.rows));
  useEffect(() => { load(); }, []);
  const create = async () => { const { data } = await api.post("/apikeys", { name, scope, expires_days: days ? Number(days) : null }); setShown(data.key); setName(""); load(); };
  return (
    <div className="panel p-4 space-y-2" data-testid="apikeys-settings">
      <div className="text-sm font-medium flex items-center gap-2"><KeyRound size={14} />API keys</div>
      <div className="text-xs text-neutral-500">For Claude and Zapier. Send <code className="kbd">Authorization: Bearer &lt;key&gt;</code> (or X-API-Key) to {BASE}/api. MCP server: <code className="kbd" data-testid="mcp-url">{BASE}/api/mcp</code>. Every key acts as the service user "Claude (API)"; all its writes are audited.</div>
      <div className="text-xs text-neutral-500" data-testid="inbound-lead-help">New leads from Zapier / Typeform: POST flat JSON to <code className="kbd" data-testid="inbound-lead-url">{BASE}/api/inbound/lead</code> with header <code className="kbd">X-API-Key</code> (admin key). Keys: name (or first_name + last_name), email, phone, gmv, event_date, owner, sop_id, form_title, utm_*, fbclid; every other key is saved as a custom field. Every send creates a new lead (repeat submitters show as duplicates); add <code className="kbd">merge: true</code> to update the latest lead with the same email/phone instead.</div>
      {shown && <div className="panel-2 p-2 text-xs font-mono break-all" data-testid="apikey-shown">{shown} <span className="text-neutral-500 font-sans">(copy now, shown once)</span></div>}
      {rows.map((k) => (
        <div key={k._id} className="flex items-center gap-2 text-xs" data-testid={`apikey-row-${k._id}`}>
          <span className="font-mono">{k.prefix}…</span><span className="flex-1">{k.name}</span><span className="pill">{k.scope || "admin"}</span><span className="text-neutral-500">{k.expires_at ? `expires ${k.expires_at.slice(0, 10)}` : "no expiry"}</span>
          <button className="btn-ghost p-1 hover:text-[var(--breach)]" onClick={async () => { if (!(await confirm({ title: `Revoke ${k.name}?`, body: "Anything using this key stops working immediately.", danger: true }))) return; await api.delete(`/apikeys/${k._id}`); toast.success("Key revoked"); load(); }} data-testid={`apikey-revoke-${k._id}`}><Trash2 size={12} /></button>
        </div>
      ))}
      <div className="flex flex-wrap gap-2"><input className={`${inp} flex-1`} placeholder="Key name, e.g. Claude workspace" value={name} onChange={(e) => setName(e.target.value)} data-testid="apikey-name-input" />
        <select className={inp} value={scope} onChange={(e) => setScope(e.target.value)} data-testid="apikey-scope-select"><option value="admin">Admin</option><option value="read">Read-only</option></select>
        <input type="number" className={`${inp} w-28`} placeholder="Expiry (days)" value={days} onChange={(e) => setDays(e.target.value)} data-testid="apikey-expiry-input" />
        <button className="btn" disabled={!name.trim()} onClick={create} data-testid="apikey-create-btn"><Plus size={13} />Create</button></div>
    </div>
  );
}

function EventHooks() {
  return (
    <div className="panel p-4 space-y-2" data-testid="event-hooks-settings">
      <div className="text-sm font-medium">Outbound webhooks</div>
      <div className="text-xs text-neutral-500">Now in CRM setup → Webhooks: workflow rules on lead created, any field change or SOP outcome. <a className="underline" href="/crm/setup?tab=webhooks" data-testid="event-hooks-link">Open</a></div>
    </div>
  );
}

function ConnectedApps() {
  const { confirm } = useConfirm();
  const [rows, setRows] = useState([]);
  const load = () => api.get("/oauth/connections").then((r) => setRows(r.data.rows)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <div className="panel p-4 space-y-2" data-testid="oauth-connections">
      <div className="text-sm font-medium">Claude connector (OAuth)</div>
      <div className="text-xs text-neutral-500">In claude.ai: Settings → Connectors → Add custom connector → paste <code className="kbd" data-testid="oauth-mcp-url">{BASE}/api/mcp</code>. Leave client ID/secret empty, then approve in the sign-in window.</div>
      {!rows.length ? <div className="text-xs text-neutral-600">No connected apps.</div> : rows.map((c) => (
        <div key={c._id} className="flex items-center gap-2 text-xs" data-testid={`oauth-conn-${c._id}`}>
          <span className="flex-1">{c.client_name}</span><span className="pill">{c.scope}</span><span className="text-neutral-500">by {c.approved_by}</span>
          <button className="btn text-xs" onClick={async () => { if (!(await confirm({ title: `Disconnect ${c.client_name}?`, danger: true }))) return; await api.delete(`/oauth/connections/${c._id}`); toast.success("Disconnected"); load(); }} data-testid={`oauth-disconnect-${c._id}`}>Disconnect</button>
        </div>
      ))}
    </div>
  );
}

function AuditLog() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/audit", { params: { limit: 50 } }).then((r) => setRows(r.data.rows)).catch(() => setRows([])); }, []);
  return (
    <div className="panel p-4 space-y-2 lg:col-span-2" data-testid="audit-log">
      <div className="text-sm font-medium">Audit log (API + MCP writes)</div>
      {!rows?.length ? <div className="text-xs text-neutral-500">No API writes yet.</div> : rows.map((r, i) => (
        <div key={i} className="text-xs border-b border-[var(--border)] py-1">
          <button className="w-full flex gap-3 text-left" onClick={() => setOpen(open === i ? null : i)} data-testid={`audit-row-${i}`}>
            <span className="text-neutral-500 w-32">{r.at?.slice(0, 16).replace("T", " ")}</span><span className="w-28 truncate">{r.actor} · {r.key_name}</span>
            <span className="font-mono w-14">{r.method}</span><span className="flex-1 truncate font-mono">{r.path}</span><span>{r.status}</span>
          </button>
          {open === i && <pre className="mt-1 p-2 panel-2 overflow-x-auto text-[10px] max-h-64">{JSON.stringify({ request: r.request, before: r.before, after: r.after }, null, 1)}</pre>}
        </div>
      ))}
    </div>
  );
}

export default function SettingsExtra({ isAdmin }) {
  return (
    <section className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="settings-extra">
      <Calendly isAdmin={isAdmin} />
      <ConfirmReminders isAdmin={isAdmin} />
      <Fathom />
      {isAdmin && <ApiKeys />}
      {isAdmin && <ConnectedApps />}
      <EventHooks />
      {isAdmin && <AuditLog />}
    </section>
  );
}
